import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, BadgeCheck, Mail, Send, Smartphone, Undo2 } from 'lucide-react'
import {
  useInvoice, useInvoiceAction, useInvoicePayments, useRecordPayment, useReversePayment,
  useSendInvoiceEmail, useSendInvoiceSms
} from '@/lib/queries.js'
import { useTenant } from '@/app/TenantProvider.jsx'
import { money, formatDate, humanise } from '@/lib/format.js'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardBody, CardHeader, CardTitle, DetailRow, PageHeader } from '@/components/ui/card.jsx'
import { Alert, Spinner } from '@/components/ui/feedback.jsx'
import { StatusBadge } from '@/components/ui/badge.jsx'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog.jsx'
import { Field, Input } from '@/components/ui/field.jsx'
import { Select, Textarea } from '@/components/ui/select.jsx'
import { humanMessage, isWorkflow } from '@/lib/errors.js'

/**
 * Invoice detail (Phase 1b screen 8, extended by Phase 2 items 11/13/16).
 *
 * ── THE COPY RULE, UNCHANGED AND NOW MORE IMPORTANT ─────────────────────────
 * MBOS still does not charge anyone from this screen. Recording a payment is
 * BOOKKEEPING for money collected somewhere else — a cheque, cash, a bank
 * transfer, the contractor's own terminal. Every string below says "record",
 * never "collect" or "charge". A contractor who believes MBOS took their
 * client's money has been actively misled, which is worse than a missing
 * feature, and it is a belief that gets tested at the worst possible moment.
 *
 * ── WHAT PHASE 2 ADDED ──────────────────────────────────────────────────────
 * · A real payment history, because `partial` is now a state an invoice can
 *   actually be in and "paid $400 of $1,000" is meaningless without the rows
 *   behind it.
 * · Recording replaced marking. `paid` is DERIVED from the ledger now and the
 *   status route refuses it, so there is no "mark as paid" to press.
 * · A refund action, OWNER ONLY and MANUAL PAYMENTS ONLY. Both limits are
 *   enforced server-side; the UI hides rather than duplicates them, so a
 *   contractor is not offered a button that exists to fail.
 * · Overpayment shows as overpayment. A negative balance is a real state
 *   (D25.4) and rendering it as a negative number owed would be a lie.
 */
export function InvoiceDetail () {
  const { id } = useParams()
  const { isOwner, hasFeature } = useTenant()
  const { data: invoice, isPending, error } = useInvoice(id)
  const { data: history } = useInvoicePayments(id)
  const send = useInvoiceAction('send')
  const record = useRecordPayment()
  const reverse = useReversePayment()
  const sendEmail = useSendInvoiceEmail()
  const sendSms = useSendInvoiceSms()

  const [actionError, setActionError] = React.useState(null)
  const [recordOpen, setRecordOpen] = React.useState(false)
  const [reversing, setReversing] = React.useState(null)
  const [emailOpen, setEmailOpen] = React.useState(false)
  const [emailTo, setEmailTo] = React.useState('')
  const [emailSent, setEmailSent] = React.useState(false)
  const [smsOpen, setSmsOpen] = React.useState(false)
  const [smsTo, setSmsTo] = React.useState('')
  const [smsSent, setSmsSent] = React.useState(false)

  if (isPending) {
    return <div className="flex justify-center py-16"><Spinner label="Loading invoice" /></div>
  }

  if (error) {
    return (
      <>
        <PageHeader back={<BackLink />} title="Could not load this invoice" />
        <Alert tone="danger">{humanMessage(error)}</Alert>
      </>
    )
  }

  async function run (fn) {
    setActionError(null)
    try { await fn() } catch (err) { setActionError(err) }
  }

  const closed = ['written_off', 'voided'].includes(invoice.status)
  const canSend = invoice.status === 'draft'
  const canRecord = !closed
  // Item 17 (D28a): mirrors the backend guard exactly — a draft has no
  // customer-facing version to email yet, matching sendInvoiceEmail's own
  // not_draft refusal.
  const canEmail = invoice.status !== 'draft'

  async function submitEmail (event) {
    event.preventDefault()
    setEmailSent(false)
    try {
      await sendEmail.mutateAsync({ invoiceId: invoice.id, to: emailTo.trim() })
      setEmailSent(true)
      setEmailTo('')
    } catch { /* rendered from sendEmail.error below */ }
  }

  async function submitSms (event) {
    event.preventDefault()
    setSmsSent(false)
    try {
      await sendSms.mutateAsync({ invoiceId: invoice.id, to: smsTo.trim() })
      setSmsSent(true)
      setSmsTo('')
    } catch {}
  }
  const balance = Number(invoice.balance)
  const overpaid = balance < 0
  const payments = history?.payments ?? []

  return (
    <>
      <PageHeader
        back={<BackLink jobId={invoice.job_id} />}
        title={invoice.invoice_number}
        description={<StatusBadge status={invoice.status} />}
        actions={
          <>
            {canSend && (
              <Button
                variant="secondary"
                loading={send.isPending}
                onClick={() => run(() => send.mutateAsync({ id: invoice.id }))}
                data-testid="send-invoice"
              >
                <Send /> Send
              </Button>
            )}
            {canEmail && (
              <Button
                variant="secondary"
                onClick={() => { setEmailSent(false); setEmailOpen(true) }}
                data-testid="email-invoice"
              >
                <Mail /> Email it
              </Button>
            )}
            {canEmail && (
              <Button
                variant="secondary"
                onClick={() => { setSmsSent(false); setSmsOpen(true) }}
                data-testid="sms-invoice"
              >
                <Smartphone /> Text it
              </Button>
            )}
            {canRecord && (
              <Button onClick={() => setRecordOpen(true)} data-testid="record-payment">
                <BadgeCheck /> Record a payment
              </Button>
            )}
          </>
        }
      />

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent title="Email this invoice">
          <form onSubmit={submitEmail} className="space-y-4">
            {sendEmail.error && <Alert tone="danger">{humanMessage(sendEmail.error)}</Alert>}
            {emailSent && <Alert tone="success">Sent.</Alert>}
            <Field
              id="invoice-email-to"
              label="Customer's email"
              type="email"
              value={emailTo}
              onChange={e => setEmailTo(e.target.value)}
              placeholder="customer@example.com"
              autoCapitalize="none" autoCorrect="off"
              required
              disabled={sendEmail.isPending}
              data-testid="invoice-email-to"
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEmailOpen(false)}>Close</Button>
              <Button type="submit" loading={sendEmail.isPending} disabled={!emailTo.trim()} data-testid="invoice-email-submit">
                <Mail /> Send
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={smsOpen} onOpenChange={setSmsOpen}>
        <DialogContent title="Text this invoice">
          <form onSubmit={submitSms} className="space-y-4">
            {sendSms.error && <Alert tone="danger">{humanMessage(sendSms.error)}</Alert>}
            {smsSent && <Alert tone="success">Text sent.</Alert>}
            <Field
              id="invoice-sms-to"
              label="Customer's phone"
              type="tel"
              value={smsTo}
              onChange={e => setSmsTo(e.target.value)}
              placeholder="+15550101234"
              autoCapitalize="none" autoCorrect="off"
              required
              disabled={sendSms.isPending}
              data-testid="invoice-sms-to"
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setSmsOpen(false)}>Close</Button>
              <Button type="submit" loading={sendSms.isPending} disabled={!smsTo.trim()} data-testid="invoice-sms-submit">
                <Smartphone /> Send
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {actionError && (
        <Alert tone={isWorkflow(actionError) ? 'warn' : 'danger'} className="mb-4">
          {humanMessage(actionError)}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader><CardTitle>Line items</CardTitle></CardHeader>
            <CardBody className="p-0">
              <ul className="divide-y divide-line">
                {(invoice.line_items ?? []).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 px-4 py-3 sm:px-5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-ink">{item.description}</p>
                      <p className="tabular text-xs text-ink-subtle">
                        {item.quantity} × {money(item.unit_price)}
                        {item.taxable === false && ' · not taxable'}
                      </p>
                    </div>
                    <span className="tabular text-sm font-medium text-ink">{money(item.total)}</span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          <PaymentHistory
            payments={payments}
            isOwner={isOwner}
            onReverse={setReversing}
          />

          {invoice.notes_client && (
            <Card>
              <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
              <CardBody>
                <p className="whitespace-pre-wrap text-sm text-ink">{invoice.notes_client}</p>
              </CardBody>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Amount</CardTitle></CardHeader>
            <CardBody className="space-y-2">
              <SummaryRow label="Subtotal" value={invoice.subtotal} />
              {Number(invoice.tax_amount) > 0 && <SummaryRow label="Tax" value={invoice.tax_amount} />}
              <div className="border-t border-line pt-2">
                <SummaryRow label="Total" value={invoice.total} bold testId="invoice-total" />
              </div>
              <SummaryRow label="Recorded" value={invoice.amount_paid} testId="invoice-paid" />
              <div className="border-t border-line pt-2">
                {/* An overpayment is a real outcome, not a negative debt.
                    Showing "Balance −$200" reads as a rounding bug; showing
                    "Overpaid $200" reads as the thing that actually happened
                    and points at what to do about it. */}
                <SummaryRow
                  label={overpaid ? 'Overpaid' : 'Balance'}
                  value={overpaid ? Math.abs(balance) : balance}
                  bold
                  testId="invoice-balance"
                />
              </div>
            </CardBody>
          </Card>

          {invoice.status === 'partial' && (
            <Alert tone="info" title="Part paid">
              {money(invoice.amount_paid)} of {money(invoice.total)} recorded.
              The status follows the payments — it is not set by hand.
            </Alert>
          )}

          {overpaid && (
            <Alert tone="warn" title="More recorded than billed">
              {money(Math.abs(balance))} more has been recorded than this invoice
              bills for. Refund the difference from the payment history, or add
              the extra work as line items.
            </Alert>
          )}

          {invoice.status === 'refunded' && (
            <Alert tone="warn" title="Refunded">
              Everything recorded against this invoice has been given back.
              Nothing is owed.
            </Alert>
          )}

          <Card>
            <CardHeader><CardTitle>Details</CardTitle></CardHeader>
            <CardBody>
              <dl className="space-y-2.5">
                <DetailRow label="Terms">{humanise(invoice.payment_terms)}</DetailRow>
                <DetailRow label="Due">{invoice.due_date ? formatDate(invoice.due_date) : null}</DetailRow>
                <DetailRow label="Sent">{invoice.sent_at ? formatDate(invoice.sent_at) : null}</DetailRow>
                <DetailRow label="From estimate">
                  {invoice.estimate_id
                    ? <Link to={`/estimates/${invoice.estimate_id}`} className="text-brand hover:underline">
                        View estimate
                      </Link>
                    : null}
                </DetailRow>
              </dl>
            </CardBody>
          </Card>

          {/* The online pay link lives behind a RELEASE switch, off by default
              and enforced server-side as well as here. Until a webhook
              receiver exists, a customer can pay successfully on the
              processor's page and this invoice still reads unpaid — which is
              worse than not offering the button. */}
          {hasFeature('public_pay_link') && (
            <Alert tone="info" title="Online card payments">
              Card payments are switched on for this deployment. Recorded
              payments and card payments both land in the history above.
            </Alert>
          )}
        </div>
      </div>

      <RecordPaymentDialog
        open={recordOpen}
        onOpenChange={setRecordOpen}
        invoice={invoice}
        pending={record.isPending}
        onSubmit={body => run(async () => {
          await record.mutateAsync({ invoiceId: invoice.id, ...body })
          setRecordOpen(false)
        })}
      />

      <ReverseDialog
        payment={reversing}
        onOpenChange={open => !open && setReversing(null)}
        pending={reverse.isPending}
        onSubmit={body => run(async () => {
          await reverse.mutateAsync({ paymentId: reversing.id, ...body })
          setReversing(null)
        })}
      />
    </>
  )
}

/**
 * The payment history.
 *
 * Reversals are nested UNDER the payment they reverse rather than listed
 * alongside it, because "refunded $200 of the $500 cheque" is the sentence a
 * contractor is trying to read. A flat list of positive numbers with a type
 * column makes them do the arithmetic.
 */
function PaymentHistory ({ payments, isOwner, onReverse }) {
  return (
    <Card>
      <CardHeader><CardTitle>Payment history</CardTitle></CardHeader>
      <CardBody className={payments.length ? 'p-0' : undefined}>
        {!payments.length
          ? (
            <p className="text-sm text-ink-muted">
              Nothing recorded yet. Use “Record a payment” when you take a
              cheque, cash or a bank transfer.
            </p>
            )
          : (
            <ul className="divide-y divide-line">
              {payments.map(p => (
                <li key={p.id} className="px-4 py-3 sm:px-5">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-ink">
                        {humanise(p.method)} · {humanise(p.payment_type)}
                        {p.reference && <span className="text-ink-subtle"> · {p.reference}</span>}
                      </p>
                      <p className="tabular text-xs text-ink-subtle">{formatDate(p.paid_at)}</p>
                    </div>
                    <span className="tabular text-sm font-medium text-ink">{money(p.amount)}</span>
                  </div>

                  {(p.reversals ?? []).map(r => (
                    <div key={r.id} className="mt-2 flex items-start gap-3 border-l-2 border-line pl-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-ink-muted">
                          {r.payment_type === 'void' ? 'Cancelled' : 'Refunded'}
                        </p>
                        <p className="tabular text-xs text-ink-subtle">{formatDate(r.paid_at)}</p>
                      </div>
                      <span className="tabular text-sm text-ink-muted">−{money(r.amount)}</span>
                    </div>
                  ))}

                  {/* Two limits, both server-enforced, both hidden here
                      rather than duplicated as client-side logic:
                        · OWNER ONLY (item 12). A member sees no button.
                        · MANUAL PAYMENTS ONLY (D24.7). A card payment cannot
                          be returned without the processor, and that is not
                          built — so the row says so instead of offering an
                          action that exists to fail. */}
                  {isOwner && Number(p.refundable_remaining) > 0 && (
                    p.reversal?.available
                      ? (
                        <div className="mt-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onReverse(p)}
                            data-testid={`reverse-${p.id}`}
                          >
                            <Undo2 /> {p.reversal.action_label}
                          </Button>
                        </div>
                        )
                      : (
                        <p className="mt-2 text-xs text-ink-subtle">
                          Taken by card — refund it through your card processor.
                        </p>
                        )
                  )}
                </li>
              ))}
            </ul>
            )}
      </CardBody>
    </Card>
  )
}

function SummaryRow ({ label, value, bold, testId }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className={bold ? 'text-sm font-medium text-ink' : 'text-sm text-ink-muted'}>{label}</span>
      <span
        className={`tabular text-sm ${bold ? 'font-semibold text-ink' : 'text-ink'}`}
        data-testid={testId}
      >
        {money(value)}
      </span>
    </div>
  )
}

const METHODS = [
  { value: 'check', label: 'Cheque' },
  { value: 'cash', label: 'Cash' },
  { value: 'ach', label: 'Bank transfer' },
  { value: 'card', label: 'Card (taken elsewhere)' },
  { value: 'financing', label: 'Financing' },
  { value: 'other', label: 'Other' }
]

const TYPES = [
  { value: 'deposit', label: 'Deposit' },
  { value: 'milestone', label: 'Progress payment' },
  { value: 'final', label: 'Final payment' },
  { value: 'adjustment', label: 'Adjustment' }
]

/**
 * Recording a payment.
 *
 * ── THE IDEMPOTENCY KEY IS MINTED WHEN THE FORM OPENS ───────────────────────
 * Not when it submits. That is the whole point: a double-click, a retried
 * request on a flaky site connection, or a contractor pressing the button
 * again because nothing visibly happened all carry the SAME key, and the
 * server answers the second one with the first one's payment instead of
 * creating a duplicate financial record. A key minted at submit time would be
 * different every press and would guard nothing.
 */
function RecordPaymentDialog ({ open, onOpenChange, invoice, onSubmit, pending }) {
  const outstanding = Math.max(0, Number(invoice.balance))
  const [amount, setAmount] = React.useState(String(outstanding))
  const [method, setMethod] = React.useState('check')
  const [type, setType] = React.useState('deposit')
  const [reference, setReference] = React.useState('')
  const [notes, setNotes] = React.useState('')
  const [key, setKey] = React.useState(null)

  React.useEffect(() => {
    if (!open) return
    setAmount(String(Math.max(0, Number(invoice.balance))))
    setMethod('check')
    setType(Number(invoice.amount_paid) > 0 ? 'final' : 'deposit')
    setReference('')
    setNotes('')
    setKey(crypto.randomUUID())
  }, [open, invoice.balance, invoice.amount_paid])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Record a payment">
        <div className="space-y-3">
          <Alert tone="info">
            This does not charge anyone. It records money you have already
            taken — a cheque, cash, a bank transfer, or your own card terminal.
          </Alert>

          <Field id="pay-amount" label="Amount" hint={`Outstanding: ${money(outstanding)}`}>
            <Input
              id="pay-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              data-testid="payment-amount"
            />
          </Field>

          <Field id="pay-method" label="How was it paid?">
            <Select id="pay-method" value={method} onChange={e => setMethod(e.target.value)}>
              {METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Select>
          </Field>

          <Field id="pay-type" label="What kind of payment?">
            <Select id="pay-type" value={type} onChange={e => setType(e.target.value)}>
              {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </Field>

          <Field id="pay-ref" label="Reference" hint="Cheque number, transfer reference — optional">
            <Input id="pay-ref" value={reference} onChange={e => setReference(e.target.value)} />
          </Field>

          <Field id="pay-notes" label="Notes" hint="Optional, internal">
            <Textarea id="pay-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </Field>
        </div>

        <DialogFooter className="-mx-5 -mb-4 mt-4">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            loading={pending}
            data-testid="confirm-payment"
            onClick={() => onSubmit({
              amount: Number(amount),
              method,
              payment_type: type,
              reference: reference || null,
              notes: notes || null,
              idempotency_key: key
            })}
          >
            Record payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Reversing one.
 *
 * The word on the button comes from the SERVER's resolver (D24.1) — "cancel
 * payment" for a void, "refund payment" for a refund — because which one
 * applies is a property of the transaction and not a choice. In Session 1
 * every reversal MBOS can perform is a record-only refund, so the copy is
 * blunt about what pressing it does and does not do.
 */
function ReverseDialog ({ payment, onOpenChange, onSubmit, pending }) {
  const max = Number(payment?.refundable_remaining ?? 0)
  const [amount, setAmount] = React.useState('')
  const [reason, setReason] = React.useState('')
  const [key, setKey] = React.useState(null)

  React.useEffect(() => {
    if (!payment) return
    setAmount(String(max))
    setReason('')
    // Same reasoning as recording, and it matters more here: this is the
    // submission whose duplicate would send funds twice once a processor sits
    // behind it. One key per opening of this dialog.
    setKey(crypto.randomUUID())
  }, [payment, max])

  if (!payment) return null

  return (
    <Dialog open={Boolean(payment)} onOpenChange={onOpenChange}>
      <DialogContent title={payment.reversal?.action_label ?? 'Refund payment'}>
        <div className="space-y-3">
          <Alert tone="warn">
            This records that you have given the money back. It does not move
            any money — send it to your customer the way they paid you.
          </Alert>

          <Field
            id="rev-amount"
            label="Amount to refund"
            hint={`Up to ${money(max)} of the original ${money(payment.amount)}`}
          >
            <Input
              id="rev-amount"
              type="number"
              step="0.01"
              min="0"
              max={max}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              data-testid="reversal-amount"
            />
          </Field>

          <Field id="rev-reason" label="Reason" hint="Optional, kept on the record">
            <Textarea id="rev-reason" rows={2} value={reason} onChange={e => setReason(e.target.value)} />
          </Field>
        </div>

        <DialogFooter className="-mx-5 -mb-4 mt-4">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            loading={pending}
            data-testid="confirm-reversal"
            onClick={() => onSubmit({
              amount: Number(amount),
              reason: reason || null,
              idempotency_key: key
            })}
          >
            {payment.reversal?.action_label ?? 'Refund payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function BackLink ({ jobId }) {
  return (
    <Link
      to={jobId ? `/jobs/${jobId}` : '/invoices'}
      className="mb-2 inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
    >
      <ArrowLeft className="size-4" aria-hidden="true" /> {jobId ? 'Job' : 'Invoices'}
    </Link>
  )
}
